import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export interface Notification {
  id: string;
  titleEn: string;
  titleAr: string;
  bodyEn: string;
  bodyAr: string;
  isRead: boolean;
  sentAt: string | null;
  createdAt: string;
  data?: {
    orderId?: string;
    type?: string;
    [key: string]: any;
  };
}

interface NotificationsResponse {
  notifications: Notification[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface UnreadCountResponse {
  count: number;
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['/notifications/unread-count'],
    queryFn: async (): Promise<number> => {
      try {
        const response = await apiClient.get('/notifications/unread-count');
        if (!response.ok) return 0;
        const data: UnreadCountResponse = await response.json();
        return data.count ?? 0;
      } catch {
        return 0;
      }
    },
    enabled: !!apiClient.getToken(),
    staleTime: 0,
    gcTime: 0,
  });
}

export function useNotificationHistory(page?: number, limit?: number) {
  return useQuery({
    queryKey: ['/notifications/history', { page, limit }],
    queryFn: async (): Promise<Notification[]> => {
      const params = new URLSearchParams();
      if (page) params.append('page', String(page));
      if (limit) params.append('limit', String(limit));

      const endpoint = params.toString()
        ? `/notifications/history?${params.toString()}`
        : '/notifications/history';

      const response = await apiClient.get(endpoint);
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }
      const result: NotificationsResponse | Notification[] = await response.json();
      // Handle both { notifications: [...] } and raw array shapes.
      if (Array.isArray(result)) return result;
      if (result && Array.isArray(result.notifications)) return result.notifications;
      return [];
    },
    enabled: !!apiClient.getToken(),
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const response = await apiClient.patch(`/notifications/${id}/read`);
      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/notifications/history'] });
      queryClient.invalidateQueries({ queryKey: ['/notifications/unread-count'] });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await apiClient.patch('/notifications/read-all');
      if (!response.ok) {
        throw new Error('Failed to mark all as read');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/notifications/history'] });
      queryClient.invalidateQueries({ queryKey: ['/notifications/unread-count'] });
    },
  });
}
